/** Thin fetch wrapper + react-query hooks. */
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { accessSignOut } from './access'
import { SIGN_IN, leaveFor } from './leaving'
import { markActivity } from './idle'

export class ApiError extends Error {
  constructor(status, message, detail) {
    super(message)
    this.status = status
    this.detail = detail
  }
}

/** Raised when the API cannot be reached at all, as opposed to returning an error. */
export class ApiUnreachableError extends ApiError {
  constructor(detail) {
    super(
      0,
      'The API is not reachable. Is the Worker running?',
      detail ??
        'In development the API is served by `wrangler dev`. Run `npm run dev` (which serves ' +
          'both), or if you are using the Vite dev server for hot reload, run `npm run dev:api` ' +
          'in a second terminal.'
    )
    this.unreachable = true
  }
}

/*
 * Cloudflare Access reads this header and nothing else does.
 *
 * Without it, a request made after the Access session has lapsed is answered with a
 * redirect to the login page. `fetch` follows redirects, so what arrives back here is
 * the login page — an opaque failure, or an HTML 200 that is not the JSON any caller
 * asked for, and either way a red card that says nothing about needing to sign in again.
 * With it, Access answers such a request `401` instead, which is a thing this file can
 * recognise and the app can act on. See "AJAX" in Cloudflare's session-management docs.
 *
 * It is sent at both doors rather than only the apex: the donor door is not behind
 * Access, but the header is inert there, and a request that changes meaning depending on
 * which host it goes to is worse than one extra header.
 */
const AJAX_HEADER = { 'x-requested-with': 'XMLHttpRequest' }

/**
 * The Worker's marker for a session ended by inactivity, and what to do about it.
 *
 * Access still holds a valid token at this point — the timeout is the archive's, not
 * Cloudflare's — so reloading the page would sign the person straight back in and the
 * ninety minutes would have meant nothing. Cloudflare's own logout URL is the only thing that
 * makes the next visit ask for credentials again, so that is what gets asked for. Asked,
 * not travelled to: `accessSignOut` requests it and then lands the browser on the login
 * page, this being the same way out the button takes.
 *
 * Worth knowing, because it surprises people: this ends the Access session for the whole
 * Zero Trust organisation, not this application alone. Cloudflare does not offer a
 * per-application logout. Anyone signed in to another app behind the same team domain is
 * signed out of that too.
 *
 * Guarded by beginLeaving rather than trusted to happen once: several queries can be in
 * flight when the window closes, and each of them comes back with this code. The first
 * claims the navigation and the rest must not fight it — and from that moment the screen
 * says so plainly instead of drawing the error each of them is about to throw.
 *
 * The browser now usually gets here first, on its own clock and before any request is
 * refused, which is the quieter path by far — see useIdleTimeout in lib/idle.js. This is
 * what still catches the cases that clock cannot see: a window whose account was idled
 * out from another browser, and one restored from the back-forward cache with the clock
 * of a page that has been asleep.
 */
const IDLE_CODE = 'idle_timeout'

const forceReauthentication = () => {
  leaveFor('idle', SIGN_IN, accessSignOut)
}

/**
 * A 401 the Worker did not write is Access turning an XHR away, and needs its own answer.
 *
 * Every refusal the Worker makes itself arrives as JSON with an `error` in it — see the
 * HttpError branch of worker/index.js — so a 401 with no JSON body never reached the
 * Worker. In front of the Worker exactly one thing answers 401, and that is Access
 * declining a request whose session has run out its absolute lifetime. (The AJAX header
 * above is what makes that a 401 rather than a redirect to the login page.)
 *
 * Nothing else in the app can answer it. The "You are not signed in" screen only appears
 * when /api/me fails, and /api/me is asked once, cached, and never refetched on focus, so
 * a tab left open overnight holds a perfectly good copy of the signed-in user while every
 * fresh request behind it is turned away. What that looked like was a red "Request failed
 * (401)" card on a fully drawn page — on the dashboard, and again on every screen
 * navigated to — offering a Try again that could only fail the same way. Only a document
 * navigation fixes it: Access answers *that* with a redirect to the login page and sends
 * the browser back to where it was, which is why reloading by hand always worked.
 *
 * The donor door reaches this file too but is never this case: it is not behind Access,
 * and the 401 it answers with comes from the Worker with a body. That is also why the
 * test is "the Worker did not answer" rather than a hostname — App.jsx deliberately keeps
 * the donor host out of this bundle, and one door's 401 is already distinguishable from
 * the other's without it.
 *
 * Pure, so both halves of the decision can be tested without a browser to navigate.
 */
export function reauthenticationPlan({ status, answeredByWorker, method, alreadyTried }) {
  if (status !== 401 || answeredByWorker) return 'none'
  // Straight back to another Access 401 means the reload bought no session, and a second
  // one would be the first half of a loop between this line and the login page. Better to
  // say what happened and let the next move be somebody's decision.
  if (alreadyTried) return 'ask'
  // A read can be reloaded out from under with nothing lost. A write cannot: what is in
  // the form is still the only copy of it, and reloading would take that away on top of
  // not having saved it. The write has failed either way, so the reload is offered here
  // rather than taken.
  return method === 'GET' ? 'reload' : 'ask'
}

/*
 * The flag has to survive the reload it guards, which is why it is sessionStorage and not
 * a variable: the page that comes back is the one that needs to read it. Per tab, like the
 * lapse itself, and cleared by the first request that succeeds — so a session that lapses
 * again hours later still gets its own reload instead of being told to ask.
 */
const REAUTH_KEY = 's4n.reauth-attempted'

const triedReauthentication = () => {
  try {
    return sessionStorage.getItem(REAUTH_KEY) === '1'
  } catch {
    return false
  }
}

const rememberReauthentication = (tried) => {
  try {
    if (tried) sessionStorage.setItem(REAUTH_KEY, '1')
    else sessionStorage.removeItem(REAUTH_KEY)
  } catch {}
}

/*
 * A plain reload, and not accessSignOut: Access has no session left to end here, and its
 * logout URL signs the person out of every application in the Zero Trust organisation.
 * That is a price worth paying to make the archive's own idle timeout mean something
 * (see forceReauthentication) and not worth paying for a session that has already gone.
 */
const returnThroughAccess = () => {
  // Where this is going is where it already is: Access answers a document request for the
  // current URL with the login page, and returns the browser here afterwards.
  leaveFor('expired', window.location.href, () => {
    rememberReauthentication(true)
    window.location.reload()
  })
}

/*
 * How long to wait before telling the Worker about a refusal that came from in front of
 * it — see the report at the foot of api(). A block at the edge lasts ten seconds (the
 * rate-limiting rule's own duration, README: "Keeping strangers off the donor door"), so
 * this is that plus enough to be past it rather than racing it.
 */
const EDGE_BLOCK_MS = 12_000

const LAPSED_MESSAGE = 'Your sign-in has expired'
const LAPSED_DETAIL = {
  reload: 'Sending you back through Cloudflare Access to sign in again…',
  ask: 'Reload the page to sign in again. Nothing you were part-way through was saved, so ' +
    'copy anything you have typed somewhere safe first.',
}

export async function api(path, { method = 'GET', body, formData, signal } = {}) {
  const init = { method, signal, headers: { ...AJAX_HEADER } }
  if (formData) {
    init.body = formData
  } else if (body !== undefined) {
    init.headers['content-type'] = 'application/json'
    init.body = JSON.stringify(body)
  }

  let res
  try {
    res = await fetch(`/api/${path.replace(/^\//, '')}`, init)
  } catch (err) {
    // fetch only rejects on a transport failure, e.g. nothing listening
    throw new ApiUnreachableError(err?.message)
  }

  const ct = res.headers.get('content-type') || ''
  const payload = ct.includes('application/json') ? await res.json().catch(() => null) : null

  if (!res.ok) {
    // The Vite proxy answers 5xx with an empty body when it cannot reach the
    // Worker, which is indistinguishable from a real server error unless we
    // look for the missing JSON payload.
    if (!payload && res.status >= 500) throw new ApiUnreachableError(`Proxy returned ${res.status}`)

    /*
     * Handled before anything below, and separately, because a lapsed Access session is
     * not the same kind of event as a request the archive refused. It is nobody's mistake,
     * there is nothing in it to log — the session is the very thing a report to
     * /api/errors would need in order to arrive — and the only useful response to it is a
     * trip through the login page rather than a Try again.
     */
    const plan = reauthenticationPlan({
      status: res.status,
      answeredByWorker: Boolean(payload),
      method,
      alreadyTried: triedReauthentication(),
    })
    if (plan !== 'none') {
      const lapsed = new ApiError(res.status, LAPSED_MESSAGE, LAPSED_DETAIL[plan])
      // Read by ErrorState, which offers signing in again where it would otherwise offer
      // a retry, and by anything else that would rather say this plainly than as an error.
      lapsed.sessionExpired = true
      lapsed.reported = true
      if (plan === 'reload') returnThroughAccess()
      throw lapsed
    }

    /*
     * A 429 with no body did not come from the archive, and saying so is the whole
     * value of this branch.
     *
     * Every ceiling the Worker keeps names itself when it fires — "Too many images at
     * once", "Too many searches at once" — and arrives as JSON like any other refusal
     * (see limitOrThrow). A bodyless one is Cloudflare, in front of the Worker, refusing
     * an address that has just sent a burst of requests, and it lifts itself after
     * seconds. What that looked like without this was `Request failed (429)` on a page
     * that had asked for a single record: a number, no cause, and a Try again with
     * nothing to say about whether trying again was the answer. It is.
     *
     * The other half of this fix is in components/ImageUpload.jsx, and is the half that
     * matters: a page of thumbnails no longer sends the burst in the first place.
     */
    const throttledAtEdge = res.status === 429 && !payload
    const err = throttledAtEdge
      ? new ApiError(
          429,
          'Too many requests at once',
          'Cloudflare turned this request away because this address had just sent a burst of ' +
            'them. Nothing is wrong with the record. Wait a few seconds and press Try again.'
        )
      : new ApiError(res.status, payload?.error || `Request failed (${res.status})`, payload?.detail)
    err.code = payload?.code ?? null
    // Whichever request discovers the session has lapsed is the one that acts on it.
    // Donor sessions never carry this code — that door is not behind Access, and its
    // plain 401 is already answered by the sign-in form.
    if (err.code === IDLE_CODE) forceReauthentication()
    // A JSON error body is the Worker's own error contract, which means the Worker
    // handled this and wrote it to the error log itself, with the file, the model
    // and the record beside it. Reporting it again from here would only add a
    // second, thinner row. A failure with no such body never reached the Worker —
    // an edge-level 413, a dropped connection — and is ours to report.
    err.fromApi = Boolean(payload)
    /*
     * Reported here rather than left to the caller, because the callers that report are
     * the ones doing something conspicuous — an upload, an import, a render that threw.
     * An ordinary page query had nobody reporting it at all, so an edge-level refusal on
     * a list request existed only as a red card in front of one person: a 429 with no
     * body, which the archive's own log had no row for and no way to have one. That is
     * the gap this closes, and only for failures the Worker never saw.
     *
     * Not awaited: the caller is waiting for this throw, and a report that takes a round
     * trip should not hold up the error it is about. reportClientError never throws.
     */
    if (!err.fromApi) {
      const send = () =>
        reportClientError('network', err, {
          status: res.status,
          context: { endpoint: path, method, host: location.host },
        })
      /*
       * Except when the thing refusing us is refusing /api/* wholesale, in which case a
       * report sent now goes to the same closed door as the request it is about — which
       * is why this, the one failure the paragraph above was written for, is also the one
       * that never reached the log. Sent after the block's own lifetime instead.
       *
       * The window can be gone by then and the row lost with it. A row that arrives most
       * of the time is still the whole difference from a row that cannot arrive at all.
       */
      if (throttledAtEdge) setTimeout(send, EDGE_BLOCK_MS)
      else void send()
    }
    throw err
  }
  /*
   * A request has got through, so whatever the reload was for, it worked. Cleared here
   * rather than on the way in so that the flag only ever outlives a lapse that is over.
   */
  if (triedReauthentication()) rememberReauthentication(false)
  /*
   * The Worker moves `last_seen_at` on a request it answers, so the browser's own idle
   * clock moves on one too — otherwise a page loading a long report would be counted as
   * abandoned while it was still filling itself in, and signed out mid-read.
   */
  markActivity()
  return payload
}

// ---------------------------------------------------------------- error reporting

/**
 * Tell the Worker about a failure that happened in the browser.
 *
 * Not everything that goes wrong during an import or an upload gets as far as the
 * Worker: the file can be rejected at the edge for its size, the connection can
 * drop mid-upload, a render can throw. Those failures used to exist only as a red
 * line in front of one person, so they are posted to /api/errors and land in the
 * same log as everything else.
 *
 * Never throws, and never reports its own failure — a reporter that reports itself
 * is a loop, and the one place it would run is a browser that has already lost the
 * connection it would need.
 */
let reporting = false
let reportCount = 0
const REPORT_CAP = 10

export async function reportClientError(area, err, context = {}) {
  if (reporting || reportCount >= REPORT_CAP) return
  if (err?.fromApi) return
  /*
   * api() now reports the failures the Worker never saw, and several callers report the
   * same error again on the way out — an upload that fails at the edge passes through
   * both. Marking the object is what stops one event becoming two rows. A string or a
   * frozen error cannot be marked, which is why the assignment is guarded rather than
   * assumed: an unmarkable error is reported once by whoever gets to it first and the
   * duplicate is the cost of not throwing here.
   */
  if (err?.reported) return
  if (err && typeof err === 'object') {
    try {
      err.reported = true
    } catch {}
  }
  reporting = true
  reportCount++
  try {
    await api('errors', {
      method: 'POST',
      body: {
        area,
        message: (typeof err === 'string' ? err : err?.message) || 'Something went wrong',
        detail: typeof err === 'object' ? err?.stack ?? err?.detail : null,
        status: err?.status ?? null,
        path: typeof location === 'undefined' ? null : location.pathname,
        ...context,
      },
    })
  } catch {
    // Nowhere left to report it to.
  } finally {
    reporting = false
  }
}

/** Build a query string, dropping empty values so URLs stay readable. */
export function qs(params) {
  const sp = new URLSearchParams()
  for (const [k, v] of Object.entries(params || {})) {
    if (v === undefined || v === null || v === '' || (Array.isArray(v) && !v.length)) continue
    sp.set(k, Array.isArray(v) ? v.join(',') : String(v))
  }
  const s = sp.toString()
  return s ? `?${s}` : ''
}

/*
 * `keepPreviousData: true` is the v4 spelling every caller here still uses — v5 renamed it
 * to `placeholderData: keepPreviousData` and drops the old flag silently rather than erroring,
 * so every such call kept compiling while quietly stopping doing the one thing it was there
 * for. Translated here so call sites do not all need to change spelling at once, and so a
 * query key that has never been fetched (a fresh search term, a newly chosen filter) shows
 * the previous rows while the new ones load instead of falling back to a bare loading state.
 */
/**
 * Every row a list endpoint would return for these filters, not one page of them — what a
 * CSV button exports. Pages through at the server's ceiling of 500 a request, and stops at
 * `max` so a runaway export is a long file rather than a hung tab. `total` is the server's
 * count, so a caller can say when `max` cut the file short.
 */
export async function fetchAllRows(path, params = {}, { max = 10000 } = {}) {
  const rows = []
  let total
  for (let page = 1; ; page += 1) {
    const r = await api(`${path}${qs({ ...params, page, limit: 500 })}`)
    const got = r?.rows ?? []
    rows.push(...got)
    total = r?.total ?? rows.length
    if (!got.length || rows.length >= total || rows.length >= max) break
  }
  return { rows: rows.slice(0, max), total }
}

export const useApi = (key, path, options = {}) => {
  const { keepPreviousData: keepPrev, ...rest } = options
  return useQuery({
    queryKey: Array.isArray(key) ? key : [key],
    queryFn: () => api(path),
    ...(keepPrev ? { placeholderData: keepPreviousData } : {}),
    ...rest,
  })
}

export const useMe = () => useApi('me', 'me', { staleTime: 60_000, retry: false })

export const useLookups = () =>
  useApi('lookups', 'lookups', { staleTime: 5 * 60_000 })

/** Mutation helper that invalidates the given query keys on success. */
export function useApiMutation(fn, invalidate = []) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      for (const key of invalidate) qc.invalidateQueries({ queryKey: Array.isArray(key) ? key : [key] })
    },
  })
}
